import {
  ArrowLeftRight,
  CircleStop,
  CircleX,
  ExternalLink,
  OctagonAlert,
  RotateCcw,
  Send,
} from "lucide-react";
import { useState } from "react";
import { estimateLabel } from "../../estimate";
import { useCancelRun, useFollowUp, useRetryTask } from "../../hooks/use-actions";
import { duration, timeUntil, usd } from "../../lib/format";
import { useFactory } from "../../store";
import type { AgentSummary, HandoffContext, Run, RunListItem, Task } from "../../types";
import { RunTimeline } from "../run-timeline";
import { ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { CardContent } from "../ui/card";
import { Input } from "../ui/input";

const LIVE_RUN = new Set(["queued", "blocked", "launching", "running", "waiting_for_input"]);

/**
 * The body of a station run, shared by the current-station card and an open
 * production-history row. The history trigger already shows the station,
 * status, verdict and timing, so that frame leaves those off and leads with
 * the outcome.
 */
export function StationRunDetail({
  run,
  task,
  agent,
  framed,
  timeline = true,
  actions = true,
  children,
}: {
  run: Run | RunListItem;
  task: Pick<Task, "id">;
  agent: AgentSummary | undefined;
  /** Card chrome on the current station; a plain panel inside a history row. */
  framed: "card" | "panel";
  /** The run's event log and provider link. `/runs/:id/events` is admin only. */
  timeline?: boolean;
  /** Follow-up, cancel and retry, which only admins may call. */
  actions?: boolean;
  children?: React.ReactNode;
}) {
  const { agentById, state } = useFactory();
  const [message, setMessage] = useState("");
  const followUp = useFollowUp(task.id);
  const cancel = useCancelRun(task.id);
  const retry = useRetryTask(task.id);
  const structured = "structured" in run ? run.structured : null;
  const handoff = "handoff" in run ? run.handoff : null;
  const estimate = estimateLabel(structured);
  const live = LIVE_RUN.has(run.status);
  const canFollowUp =
    actions && agent?.kind === "llm" && ["completed", "running", "waiting_for_input"].includes(run.status);
  const canCancel = actions && live;
  const canRetry = actions && ["failed", "expired", "cancelled", "blocked"].includes(run.status);
  const otherProviders = Object.entries(state?.providers ?? {})
    .filter(([id, p]) => p.configured && id !== run.provider && id !== "simulator")
    .map(([id]) => id);
  const fromName = handoff ? (agentById.get(handoff.fromAgentId)?.name ?? handoff.fromAgentId) : null;
  const showIdentity = framed === "card";
  const hasActions = canFollowUp || canCancel || canRetry;
  const elapsed = duration(run.startedAt, run.finishedAt);

  const body = (
    <div className="space-y-3">
      {run.blockedReason && (
        <Alert className="border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-200">
          <OctagonAlert />
          <AlertTitle>Blocked</AlertTitle>
          <AlertDescription className="text-amber-800/90 dark:text-amber-200/90">
            {run.blockedReason}
          </AlertDescription>
        </Alert>
      )}
      {run.error && run.status !== "blocked" && (
        <Alert variant="destructive">
          <CircleX />
          <AlertTitle>Run failed</AlertTitle>
          <AlertDescription>{run.error}</AlertDescription>
        </Alert>
      )}

      {run.summary && <p className="text-sm leading-snug">{run.summary}</p>}
      {handoff && <Handoff handoff={handoff} fromName={fromName} />}

      <MetaLine
        items={[
          showIdentity ? { id: "provider", node: run.provider } : null,
          agent?.model ? { id: "model", node: <MetaValue label="model">{agent.model}</MetaValue> } : null,
          showIdentity && run.attempt > 1
            ? { id: "attempt", node: <ToneBadge tone="amber">Attempt {run.attempt}</ToneBadge> }
            : null,
          showIdentity && run.verdict
            ? { id: "verdict", node: <ToneBadge tone="violet">{run.verdict}</ToneBadge> }
            : null,
          showIdentity && estimate
            ? { id: "estimate", node: <ToneBadge tone="sky">{estimate}</ToneBadge> }
            : null,
          showIdentity ? { id: "started", node: <TimeAgo at={run.startedAt ?? run.createdAt} /> } : null,
          showIdentity && elapsed !== "—" ? { id: "duration", node: elapsed } : null,
          run.usage?.costUsd !== undefined ? { id: "cost", node: usd(run.usage.costUsd) } : null,
          run.usage?.totalTokens !== undefined
            ? { id: "tokens", node: `${run.usage.totalTokens.toLocaleString()} tokens` }
            : null,
          run.deadlineAt && !run.finishedAt
            ? { id: "deadline", node: `due ${timeUntil(run.deadlineAt)}` }
            : null,
          run.mcpServers.length > 0
            ? { id: "tools", node: <MetaValue label="tools">{run.mcpServers.join(", ")}</MetaValue> }
            : null,
        ]}
      />

      {timeline && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">Timeline</span>
            {typeof run.providerMeta.url === "string" && (
              <a
                href={run.providerMeta.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Open in {run.provider} <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          <RunTimeline runId={run.id} live={live} className={live ? "h-40" : "h-28"} />
        </div>
      )}

      {children && <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{children}</div>}

      {hasActions && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          {canFollowUp && (
            <form
              className="flex min-w-48 flex-1 gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!message.trim()) return;
                followUp.mutate({ runId: run.id, message }, { onSuccess: () => setMessage("") });
              }}
            >
              <Input
                placeholder={`Follow up with ${agent?.name ?? "the agent"}…`}
                aria-label="Follow-up message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="h-7 bg-background text-sm"
              />
              <Button type="submit" size="sm" disabled={!message.trim() || followUp.isPending}>
                <Send /> Send
              </Button>
            </form>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => cancel.mutate(run.id)}
              disabled={cancel.isPending}
            >
              <CircleStop /> Cancel
            </Button>
          )}
          {canRetry && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => retry.mutate({ agentId: run.agentId })}
                disabled={retry.isPending}
              >
                <RotateCcw /> Retry
              </Button>
              {otherProviders.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant="outline"
                  onClick={() => retry.mutate({ agentId: run.agentId, provider: p })}
                  disabled={retry.isPending}
                >
                  <RotateCcw /> Retry on {p}
                </Button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );

  if (framed === "card") return <CardContent>{body}</CardContent>;
  return <div className="p-3">{body}</div>;
}

function MetaValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="text-muted-foreground">{label}</span> {children}
    </>
  );
}

function MetaLine({ items }: { items: Array<{ id: string; node: React.ReactNode } | null> }) {
  const shown = items.filter((item): item is { id: string; node: React.ReactNode } => item != null);
  if (shown.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
      {shown.map((item, index) => (
        <span key={item.id} className="inline-flex items-center gap-1.5">
          {index > 0 && <span aria-hidden="true">·</span>}
          <span className="text-foreground">{item.node}</span>
        </span>
      ))}
    </p>
  );
}

function Handoff({ handoff, fromName }: { handoff: HandoffContext; fromName: string | null }) {
  const estimate = handoff.estimate;
  return (
    <div className="rounded-md bg-muted/50 px-2.5 py-2 text-sm">
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium">
          <ArrowLeftRight className="size-3" /> From {fromName}
        </span>
        {estimate && (
          <span>
            {estimate.storyPoints} pts · {estimate.developmentImpact} dev, {estimate.businessImpact} business,{" "}
            {estimate.testingImpact} test · {estimate.sla}
          </span>
        )}
        {handoff.branch && <span className="font-mono">{handoff.branch}</span>}
        {handoff.prUrl && (
          <a href={handoff.prUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
            {prLabel(handoff.prUrl)}
          </a>
        )}
      </p>
      {handoff.summary && <p className="mt-1 leading-snug">{handoff.summary}</p>}
      {handoff.findings && handoff.findings.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
          {handoff.findings.map((finding) => (
            <li key={finding}>{finding}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function prLabel(url: string): string {
  const match = url.match(/\/pull\/(\d+)/);
  return match ? `PR #${match[1]}` : "Pull request";
}
